/**
 * @fileoverview Service para operações com pastas/processos.
 *
 * Centraliza todas as chamadas de API relacionadas a pastas,
 * incluindo listagem por caixa (inbox, sent, archived), tramitação,
 * recebimento, recusa e arquivamento.
 *
 * Implementa cache local (30s TTL) para otimizar requisições frequentes.
 * Requisições com ordenação (sort) não são cacheadas.
 *
 * @module services/FoldersService
 * @see {@link module:vuex/folders} para state management
 * @see {@link module:typedef} para definições de tipos
 */

import axios from 'axios';
import API_V2_URL from '@/constants/API_V2_URL';
import headers from '@/constants/headers';

/** @type {Map<string, {data: any, timestamp: number}>} Cache de requisições */
const requestCache = new Map();

/** @constant {number} TTL do cache em milissegundos (30 segundos) */
const CACHE_TTL = 30000;

/**
 * Remove entradas expiradas do cache.
 * Executada automaticamente a cada minuto.
 * @private
 */
const clearExpiredCache = () => {
  const now = Date.now();
  for (const [key, value] of requestCache.entries()) {
    if (now - value.timestamp > CACHE_TTL) {
      requestCache.delete(key);
    }
  }
};

// Limpa cache expirado a cada minuto
setInterval(clearExpiredCache, 60000);

axios.defaults.headers.common.Authorization = `Bearer ${headers.access_token}`;

/**
 * Service de pastas/processos.
 * @namespace foldersService
 */
const foldersService = {
  /**
   * Realiza requisição GET com cache opcional.
   * Requisições com ordenação não são cacheadas para garantir dados atualizados.
   *
   * @param {string} url - URL completa da requisição
   * @param {Object} [options={}] - Opções do axios
   * @returns {Promise<import('axios').AxiosResponse>} Resposta da API
   * @private
   */
  async makeRequest(url, options = {}) {
    // Não usar cache para requisições que incluem ordenação
    if (url.includes('order=') && url.includes('sort=')) {
      try {
        const response = await axios.get(url, options);
        return response;
      } catch (error) {
        console.error('Erro na requisição:', error);
        throw error;
      }
    }

    // Para outras requisições, usar cache normalmente
    const cacheKey = `${url}-${JSON.stringify(options)}`;
    const cachedResponse = requestCache.get(cacheKey);

    if (cachedResponse && Date.now() - cachedResponse.timestamp < CACHE_TTL) {
      return cachedResponse.data;
    }

    try {
      const response = await axios.get(url, options);
      requestCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });
      return response;
    } catch (error) {
      console.error('Erro na requisição:', error);
      throw error;
    }
  },

  /**
   * Limpa todo o cache de requisições manualmente.
   * Útil após operações que alteram estado (tramitação, arquivamento).
   */
  clearCache() {
    requestCache.clear();
  },

  /**
   * Busca pastas recebidas (API V1 - legado).
   * @returns {Promise<import('axios').AxiosResponse>}
   * @deprecated Usar getFoldersInbox com paginação
   */
  getFoldersReceived() {
    return axios.get('folders/folders/received');
  },

  /**
   * Busca pastas por texto em toda a organização.
   * Requer permissão de busca geral.
   *
   * @param {number} page - Página atual (1-indexed)
   * @param {number} limit - Itens por página
   * @param {string} [searchText=''] - Texto de busca (número ou assunto)
   * @returns {Promise<import('axios').AxiosResponse<{folders: Folder[], total_count: number}>>}
   */
  getFoldersSearch(page, limit, searchText = '') {
    return axios.get(`${API_V2_URL()}/folders/folders/search?page=${page}&limit=${limit}&q=${searchText}`);
  },

  /**
   * Busca pastas da caixa de entrada do setor ativo.
   * Inclui pastas recebidas e pendentes de recebimento.
   *
   * @param {number} page - Página atual
   * @param {number} limit - Itens por página
   * @param {string} order - Direção da ordenação ('asc'|'desc')
   * @param {string} sort - Campo de ordenação (ex: 'effective_updated_at')
   * @param {string} [searchText=''] - Filtro por texto
   * @param {string} [folderTypeId=''] - Filtro por tipo de pasta
   * @returns {Promise<import('axios').AxiosResponse<{folders: Folder[], total_count: number}>>}
   */
  getFoldersInbox(page, limit, order, sort, searchText = '', folderTypeId = '') {
    const url = `${API_V2_URL()}/folders/folders/inbox?page=${page}&limit=${limit}&order=${order}&sort=${sort}&q=${searchText}&folder_type_id=${folderTypeId}`;
    return this.makeRequest(url);
  },

  /**
   * Busca pastas não recebidas (aguardando aceite).
   *
   * @param {number} page - Página atual
   * @param {number} limit - Itens por página
   * @param {string} order - Direção da ordenação
   * @param {string} sort - Campo de ordenação
   * @param {string} [searchText=''] - Filtro por texto
   * @param {string} [folderTypeId=''] - Filtro por tipo de pasta
   * @returns {Promise<import('axios').AxiosResponse<{folders: Folder[], total_count: number}>>}
   */
  getFoldersUnreceived(page, limit, order, sort, searchText = '', folderTypeId = '') {
    const url = `${API_V2_URL()}/folders/folders/unreceived?page=${page}&limit=${limit}&order=${order}&sort=${sort}&q=${searchText}&folder_type_id=${folderTypeId}`;
    return this.makeRequest(url);
  },

  /**
   * Busca pastas de uma pasta customizada do usuário.
   *
   * @param {number} id - ID da pasta customizada
   * @param {number} page - Página atual
   * @param {number} limit - Itens por página
   * @param {string} order - Direção da ordenação
   * @param {string} sort - Campo de ordenação
   * @param {string} [searchText=''] - Filtro por texto
   * @param {string} [folderTypeId=''] - Filtro por tipo de pasta
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  getFoldersCustom(id, page, limit, order, sort, searchText = '', folderTypeId = '') {
    return axios.get(`${API_V2_URL()}/folders/folder_customs/${id}?page=${page}&limit=${limit}&order=${order}$sort=${sort}&q=${searchText}&folder_type_id=${folderTypeId}`);
  },

  /**
   * Busca pastas de um funcionário específico.
   * Usado na tela de administração de usuários.
   *
   * @param {Object} params - Parâmetros de busca
   * @param {number} params.id - ID do funcionário
   * @param {number} [params.page=1] - Página atual
   * @param {number} [params.limit=20] - Itens por página
   * @param {string} [params.order='desc'] - Direção
   * @param {string} [params.sort='created_at'] - Campo
   * @param {string} [params.searchText=''] - Filtro
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  getEmployeeFolders({ id, page = 1, limit = 20, order = 'desc', sort = 'created_at', searchText = '' }) {
    return axios.get(`${API_V2_URL()}/folders/folders/employee/${id}?page=${page}&limit=${limit}&order=${order}&sort=${sort}&q=${searchText}`);
  },

  /**
   * Busca tipos de pasta presentes na inbox do setor.
   * Usado para popular filtro de tipos.
   *
   * @returns {Promise<import('axios').AxiosResponse<FolderType[]>>}
   */
  getFolderTypesOnInbox() {
    return axios.get(`${API_V2_URL()}/folders/folders/folder_types_on_inbox`);
  },

  /**
   * Busca pastas enviadas pelo setor ativo.
   *
   * @param {number} page - Página atual
   * @param {number} limit - Itens por página
   * @param {string} [searchText=''] - Filtro por texto
   * @param {string} [administrativeUnitId=''] - ID do setor (obsoleto)
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  getFoldersSent(page, limit, searchText = '', administrativeUnitId = '') {
    const url = `${API_V2_URL()}/folders/folders/sent?page=${page}&limit=${limit}&q=${searchText}`;
    return this.makeRequest(url);
  },

  /**
   * Busca pastas arquivadas.
   *
   * @param {number} page - Página atual
   * @param {number} limit - Itens por página
   * @param {string} [searchText=''] - Filtro por texto
   * @param {string} [administrativeUnitId=''] - ID do setor (obsoleto)
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  getFoldersArchived(page, limit, searchText = '', administrativeUnitId = '') {
    const url = `${API_V2_URL()}/folders/folders/archived?page=${page}&limit=${limit}&q=${searchText}`;
    return this.makeRequest(url);
  },

  /**
   * Busca pastas com documentos pendentes de assinatura.
   * @returns {Promise<import('axios').AxiosResponse<{folders: Folder[]}>>}
   */
  getFoldersToSign() {
    return axios.get(`${API_V2_URL()}/folders/folders/pendency/signatures`);
  },

  /**
   * Busca pastas com minutas pendentes de revisão.
   * @returns {Promise<import('axios').AxiosResponse<{folders: Folder[]}>>}
   */
  getFoldersToReview() {
    return axios.get(`${API_V2_URL()}/folders/folders/pendency/revisions`);
  },

  /**
   * Busca pastas com pendências por tipo.
   *
   * @param {string} type - Tipo de pendência ('signatures'|'revisions')
   * @param {string} [queryString=''] - Query string adicional
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  getPendencyFolders(type, queryString = '') {
    const url = `${API_V2_URL()}/folders/folders/pendency/${type}${queryString ? '?' + queryString : ''}`;
    return axios.get(url);
  },

  /**
   * Busca contagens de pastas por caixa.
   * Usado para badges nos menus laterais.
   *
   * @param {string} [administrativeUnitId=''] - ID do setor (obsoleto, usa header)
   * @returns {Promise<import('axios').AxiosResponse<{inbox: number, sent: number, unreceived: number, archived: number, signatures: number, revisions: number}>>}
   */
  getFoldersCount(administrativeUnitId = '') {
    return axios.get(`${API_V2_URL()}/folders/folders/count`);
  },

  /**
   * Busca detalhes completos de uma pasta.
   *
   * @param {number} id - ID da pasta
   * @returns {Promise<import('axios').AxiosResponse<Folder>>}
   */
  getFolder(id) {
    return axios.get(`${API_V2_URL()}/folders/folders/${id}`);
  },

  /**
   * Busca todos os tipos de pasta disponíveis (API V1).
   * @returns {Promise<import('axios').AxiosResponse<FolderType[]>>}
   */
  getFolderTypes() {
    return axios.get('folders/folder_types');
  },

  /**
   * Arquiva uma pasta (encerra processo).
   * Requer permissão de encerrar processo e assinatura digital.
   *
   * @param {number} id - ID da pasta
   * @param {Object} geolocation - Dados de geolocalização
   * @param {number} geolocation.latitude - Latitude
   * @param {number} geolocation.longitude - Longitude
   * @param {number} geolocation.accuracy - Precisão em metros
   * @param {string} signgoPassword - Senha do SignGO para assinatura
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  archiveFolder(id, geolocation, signgoPassword) {
    return axios.post(`${API_V2_URL()}/folders/folders/send_to_archive/${id}`, { geolocation, sign_go_password: signgoPassword });
  },

  /**
   * Recebe uma pasta tramitada para o setor.
   * Marca a transação como recebida e transfere propriedade.
   *
   * @param {Object} payload - Dados do recebimento
   * @param {number} payload.folder_id - ID da pasta
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  receiveFolder(payload) {
    return axios.post(`${API_V2_URL()}/folders/folders/receive_folder`, payload);
  },

  /**
   * Adiciona interessado a uma pasta.
   *
   * @param {number} folderId - ID da pasta
   * @param {Object} payload - Dados do interessado
   * @param {string} payload.interestable_type - Tipo ('User'|'Citizen')
   * @param {number} payload.interestable_id - ID do interessado
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  addInterested(folderId, payload) {
    return axios.post(`${API_V2_URL()}/folders/folders/${folderId}/interesteds`, payload);
  },

  /**
   * Adiciona número adicional à pasta (protocolo externo).
   *
   * @param {Object} payload - Dados
   * @param {number} payload.folder_id - ID da pasta
   * @param {string} payload.number - Número adicional
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  addNumber(payload) {
    return axios.post('folders/folders/add_number', payload);
  },

  /**
   * Atualiza o assunto da pasta.
   *
   * @param {Object} payload - Dados
   * @param {number} payload.folder_id - ID da pasta
   * @param {string} payload.subject - Novo assunto
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  updateSubject(payload) {
    return axios.post(`${API_V2_URL()}/folders/folders/update_subject`, payload);
  },

  /**
   * Cria nova pasta/processo.
   * Gera automaticamente documento de capa e número de processo.
   *
   * @param {Object} folder - Dados da pasta
   * @param {string} folder.subject - Assunto
   * @param {number} folder.folder_type_id - ID do tipo
   * @param {number} folder.entity_id - ID da entidade
   * @param {boolean} [folder.confidential] - Se é confidencial
   * @returns {Promise<import('axios').AxiosResponse<Folder>>}
   */
  createFolder(folder) {
    return axios.post(`${API_V2_URL()}/folders/folders`, folder);
  },

  /**
   * Exclui uma pasta (soft delete).
   * Só é permitido se não houver documentos ou tramitações.
   *
   * @param {number} folderId - ID da pasta
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  destroyFolder(folderId) {
    return axios.delete(`${API_V2_URL()}/folders/folders/${folderId}`);
  },

  /**
   * Envia comentário em uma pasta.
   *
   * @param {Object} payload - Dados do comentário
   * @param {number} payload.folder_id - ID da pasta
   * @param {string} payload.content - Conteúdo do comentário
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  sendComment(payload) {
    return axios.post('folders/comments', payload);
  },

  /**
   * Busca interessados de uma pasta.
   *
   * @param {number} folderId - ID da pasta
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  getInteresteds(folderId) {
    return axios.get(`${API_V2_URL()}/folders/folders/${folderId}/interesteds`);
  },

  /**
   * Desvincula documento de uma pasta (API V1).
   *
   * @param {Object} payload - Dados
   * @param {number} payload.document_id - ID do documento
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  unlinkDocument(payload) {
    return axios.post('folders/documents/unlink_document', payload);
  },

  /**
   * Solicita download de pasta completa (ZIP).
   *
   * @param {Object} payload - Dados
   * @param {number} payload.id - ID da pasta
   * @returns {Promise<import('axios').AxiosResponse<{download_url: string}>>}
   */
  downloadFolder(payload) {
    return axios.get(`${API_V2_URL()}/folders/folders/${payload.id}/download`);
  },

  /**
   * Adiciona assinador a um documento (API V1).
   *
   * @param {Object} payload - Dados
   * @param {number} payload.document_id - ID do documento
   * @param {number} payload.user_id - ID do usuário assinador
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  addSigner(payload) {
    return axios.post('folders/documents/create_signers', payload);
  },

  /**
   * Remove solicitação de assinatura.
   *
   * @param {Object} payload - Dados
   * @param {number} payload.signer_id - ID do signer
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  removeSigner(payload) {
    return axios.post(`${API_V2_URL()}/folders/documents/destroy_signature_request`, payload);
  },

  /**
   * Busca unidades administrativas permitidas para tramitação.
   * Retorna unidades conforme fluxo definido no tipo de pasta.
   *
   * @param {number} folderId - ID da pasta
   * @returns {Promise<import('axios').AxiosResponse<AdministrativeUnit[]>>}
   */
  async permittedAdministrativeUnits(folderId) {
    return axios.get(`${API_V2_URL()}/folders/folders/${folderId}/permitted_administrative_units`);
  },

  /**
   * Recusa recebimento de pasta.
   * Gera despacho de recusa e cria transação reversa automática.
   *
   * @param {number} folderId - ID da pasta
   * @param {string} refuseReason - Justificativa da recusa (obrigatória)
   * @param {string} password - Senha SignGO para assinatura do despacho
   * @param {Object} geolocation - Dados de geolocalização
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  refuseFolder(folderId, refuseReason, password, geolocation) {
    return axios.post(`${API_V2_URL()}/folders/folders/decline_folder`, {
      folder_id: folderId,
      filled_fields: {
        content: refuseReason,
      },
      password,
      geolocation,
    });
  },

  /**
   * Tramita pasta para outro setor.
   * Gera despacho de tramitação assinado automaticamente.
   *
   * @param {number} folderId - ID da pasta
   * @param {Object} payload - Dados da tramitação
   * @param {number} payload.destination_id - ID do setor destino
   * @param {number} [payload.to_user_id] - ID do usuário destino (opcional)
   * @param {string} payload.password - Senha SignGO
   * @param {Object} payload.geolocation - Geolocalização
   * @param {Object} payload.filled_fields - Campos do despacho
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  transactFolder(folderId, payload) {
    return axios.post(`${API_V2_URL()}/folders/folders/${folderId}/transact`, payload);
  },

  /**
   * Transfere processos entre usuários.
   * Usado quando um funcionário é desativado.
   *
   * @param {Object} params - Parâmetros
   * @param {number} params.activeId - ID do usuário ativo (destino)
   * @param {number} params.inactiveId - ID do usuário inativo (origem)
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  transferProcesses({ activeId, inactiveId }) {
    return axios.post(`${API_V2_URL()}/folders/folders/transference`, { active_user_id: activeId, inactive_user_id: inactiveId });
  },

  /**
   * Gerencia anexação/desanexação de pastas.
   *
   * @param {Object} params - Parâmetros
   * @param {number} params.folderId - ID da pasta principal
   * @param {number[]} params.attachedFolders - IDs das pastas a anexar
   * @param {number[]} params.removedFolders - IDs das pastas a desanexar
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  attachFolders({ folderId, attachedFolders, removedFolders }) {
    return axios.put(`${API_V2_URL()}/folders/folders/${folderId}/attach_folders`, { add: attachedFolders, remove: removedFolders });
  },

  /**
   * Gera resumo da pasta via IA.
   *
   * @param {Object} params - Parâmetros
   * @param {number} params.folderId - ID da pasta
   * @returns {Promise<import('axios').AxiosResponse<{ai_resume: string}>>}
   */
  generateAiResume({folderId}) {
    return axios.post(`${API_V2_URL()}/folders/folders/${folderId}/generate_ai_resume`);
  }
};

export default foldersService;



// WEBPACK FOOTER //
// ./src/services/FoldersService.js